import { Mechanics } from "game-common/mechanics/mechanics"
import { Entity, EntityDimensions, TileSize } from "game-common/models"
import { arrivalLift, floorFallOffsetForRendering, floorFallZIndex } from "./effects/floor_fall"
import { MOUNT_WAIT_MS, mountOffset } from "game-common/jump"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import type { ClientRenderable } from "./entity_rendering_manager"
import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { roofGroundDepth } from "./building_depth"
import { ladderTopAt } from "game-common/room/ember/ladder_top"
import { BODY_HALF_WIDTH, BodyPlace, FALLING, LADDER_FEET_INSET, lyingPlace, pairwiseDepth, uprightPlace } from "./depth_pieces"
import { stairTravelPose } from "game-common/stair_travel"

export function initializeEntityDepth(entity: Entity, sprite: ClientRenderable, provider?: TileMapMetaProvider): void {
    if (entity.npcType !== "marker" || !entity.spriteId) return
    const offset = provider?.resolveTileMeta(entity.spriteId)?.renderZIdxOffset
    if (offset !== undefined) sprite.defaultZIndex = offset
    sprite.zIndex = entityDepth(entity, sprite.defaultZIndex, provider)
}

/**
 * Shared by active-room bodies and the projected bodies on other floors. `fromAbove`: the body's storey is drawn under
 * the viewer's (scene_room_renderer.ts), so its roofs are seen from above.
 */
export const entityDepth = (entity: Entity, defaultZIndex = 0, provider?: TileMapMetaProvider, fromAbove = false,
    fallLift?: number): number => {
    // A body on its own storey is sorted among the pieces it overlaps by their shapes (depth_pieces.ts): standing, lying,
    // climbing, in a jump or a fall, each from where it is (placeOf). The rules below give the depth it is placed nearest.
    const paired = !!provider && !fromAbove && !!entity.appearance?.race && entity.npcType !== "marker"
    // A shaft spans several floor renderings; its climber must draw over every section.
    // Standing on a stacked shaft between climbs keeps this priority until the entity steps off.
    // The shaft runs under its storey's roof in both active and projected rooms. Only the upper room's clipped
    // body section belongs above that roof; raising the lower section over it exposes both sections at once.
    if (!entity.floorFall && (entity.stairTravel || standsInShaft(provider,
        Math.floor(entity.location.x / TileSize), Math.floor(entity.location.y / TileSize)))) {
        const roof = provider ? roofOver(provider, Math.floor(entity.location.x / TileSize),
            Math.floor((entity.location.y + BODY_GROUND_OFFSET) / TileSize)) : undefined
        // Nor past what stands just in front of the ladder on its own storey: a wall there hid the climber's legs.
        const base = entity.stairTravel?.flight?.base ?? entity.location
        const front = provider && inFrontOfLadder(provider, Math.floor(base.x / TileSize), Math.floor(base.y / TileSize))
        const climbing = front === undefined ? 999998 : 100 + front * TileSize
        const sorted = paired ? pairwiseDepth(provider!, climbPlace(entity), climbing) : climbing
        return roof === undefined ? sorted : Math.min(sorted, roofGroundDepth(roof) - 0.01)
    }

    // A floor-level trap lies under everything that stands.
    if (Mechanics.entity.body.isFlat(entity)) return floorFallZIndex(entity, 0)
    const lying = entity.downed || entity.floorFall?.phase === "landed" || entity.rescuePose === "downed" || entity.washedUp
    const depth = (Mechanics.entity.body.calculateZIndexBoost(entity) + defaultZIndex || 0) - (lying ? LYING_DEPTH_BIAS : 0)
    const mount = lying ? undefined : mountDepth(entity)
    const sorted = floorFallZIndex(entity, storeyDepth(entity, mount !== undefined ? Math.max(depth, mount) : depth, provider,
        mount !== undefined, paired))
    // Lifted over every storey above, it stays over them; dropping into a hole below, under everything.
    if (!paired || sorted >= IN_THE_OPEN_DEPTH || sorted < 0) return sorted
    return pairwiseDepth(provider!, placeOf(entity, lying, mount, fallLift), sorted)
}

/**
 * Where a body is for sorting it by pairs (depth_pieces.ts BodyPlace). Lying: low and wide where it lies. In a jump up onto
 * a wall: on the ground just past the wall's row, as standing on its top from the takeoff (mountDepth), drawn over its
 * takeoff and the arc above it. Coming down from the storey above: where it lands, drawn `fallLift` px above it -- as high
 * as its sprite was drawn this frame (entity_frame.ts), or as the fall's own playback would have it.
 */
const placeOf = (entity: Entity, lying: boolean, mount: number | undefined, fallLift?: number): BodyPlace => {
    const { x, y } = entity.location, soles = y + BODY_GROUND_OFFSET
    if (lying) return lyingPlace(x, soles)
    if (mount !== undefined) return { ...uprightPlace(x, soles, 0, TileSize + 8), ground: mount - 100 + BODY_GROUND_OFFSET }
    const fall = entity.floorFall
    if (fall?.fromAbove && (fall.phase === "falling" || fall.phase === "arriving")) {
        const lift = fallLift ?? -(fall.trajectory ? floorFallOffsetForRendering(entity)
            : arrivalLift(fall, Math.max(0, Math.min(1, 1 - fall.remainingMs / Math.max(1, fall.durationMs)))))
        return uprightPlace(x, soles, Math.max(0, lift), 0, FALLING)
    }
    return uprightPlace(x, soles)
}

/**
 * A climber on its ladder's rungs: on the ground just in front of the ladder's feet (over it, behind what stands in front
 * of it), drawn as high as it has climbed.
 */
const climbPlace = (entity: Entity): BodyPlace => {
    const view = entity.stairTravel
    if (!view) return uprightPlace(entity.location.x, entity.location.y + BODY_GROUND_OFFSET)
    const pose = stairTravelPose(view, 0), lift = Math.max(0, -pose.bodyOffset)
    const base = view.flight?.base ?? pose.location
    const feet = (Math.floor(base.y / TileSize) + 1) * TileSize - LADDER_FEET_INSET
    return { ...uprightPlace(pose.location.x, pose.location.y + BODY_GROUND_OFFSET, lift), ground: feet + 1 }
}

/**
 * Over every storey's art -- the storeys above sort at their southern edge, 100 + 32 a row (building_depth.ts), under this
 * on any map under ~2800 rows -- keeping the order of the bodies drawn so among themselves; under the build cursor
 * (100000), the night fog, the leaves and the roofs: a body in front of a storey above that would otherwise be over it.
 */
export const IN_THE_OPEN_DEPTH = 90000

/**
 * The storey above is drawn as one, sorted at its southern edge, so over every body north of that: where it stands right
 * behind a body -- the upper block of a wall two storeys high -- it hid the head, and from a storey down, the face a body
 * just fell past hid it as it came down (or rose past, `rising`: in a jump up onto a wall, from the room it jumped from).
 * So a body is drawn over the storeys above while one of them stands right behind it, or it is in the air between them,
 * as long as nothing on its storey or those above stands in front of it in its column: there, the storey above keeps it
 * under. Nor under its own storey's roof (a porch before a taller storey): drawn over everything, it stood on the roof.
 * A body coming down from the storey above is drawn raised over what it drops past, and just over the edge -- soles at
 * the foot of the face below it -- its hip sorted it behind that face (whose pieces sort ~1px past such a hip): at least
 * past every piece of the row north of its soles, then.
 */
const storeyDepth = (entity: Entity, depth: number, provider?: TileMapMetaProvider, rising = false, paired = false): number => {
    if (!entity.location) return depth
    const fall = entity.floorFall
    const falling = !!fall?.fromAbove && (fall.phase === "falling" || fall.phase === "arriving")
    const x = Math.floor(entity.location.x / TileSize), row = Math.floor((entity.location.y + BODY_GROUND_OFFSET) / TileSize)
    if (falling) depth = Math.max(depth, 100 + row * TileSize + 1)
    const upper = provider?.getUpperFloorProvider?.()
    if (!provider || !upper || upper === provider) return depth
    // Sorted by pairs, a body in the air -- coming down from the storey above, or jumping up onto a wall -- is sorted
    // against each piece of the storeys round it from where it will land: over the face it drops past, under the side
    // of a storey beside it.
    if (paired && (falling || rising)) return depth
    if (!falling && !rising && !DRAWN_ART.some(layerId => drawnAt(upper, layerId, x, row - 1) &&
        !(layerId === "sprite2" && undrawnLadderTop(upper, provider, x, row - 1)))) return depth
    // Sorted by pairs (depth_pieces.ts), no roof may stand over a column the body's side reaches into either: lifted over
    // every storey beside a roof's edge one column over, the body stood on it. What stands beside it (the side of a
    // recess in a terrace) doesn't count: kept under, it lost its head to the block two storeys high behind it.
    const sides = paired ? bodyColumns(entity.location.x).filter(column => column !== x) : []
    return openInFront(provider, x, row) && [x, ...sides].every(column => roofOver(provider, column, row) === undefined)
        ? IN_THE_OPEN_DEPTH + depth / 1000 : depth
}

/** The columns a standing body's sprite reaches into, past a few px of its edge (depth_pieces.ts BODY_HALF_WIDTH). */
const bodyColumns = (hipX: number): number[] => {
    const columns: number[] = []
    for (let column = Math.floor((hipX - BODY_HALF_WIDTH + 4) / TileSize); column <= Math.floor((hipX + BODY_HALF_WIDTH - 4) / TileSize); column++)
        columns.push(column)
    return columns
}

/**
 * The southmost row of its own storey's roof over a body with its soles in row `row` of column `x`, or undefined: a roof
 * over its row or the next, as a roof is drawn a storey up (ROOF_UPLIFT_PX), across the row north of it. Roofs sort at
 * their southern row (map_rendering_manager roof groups), so that is the row the body has to stay behind.
 */
const roofOver = (provider: TileMapMetaProvider, x: number, row: number): number | undefined => {
    let y = drawnAt(provider, "sky", x, row) ? row : drawnAt(provider, "sky", x, row + 1) ? row + 1 : undefined
    if (y === undefined) return undefined
    while (drawnAt(provider, "sky", x, y + 1)) y++
    return y
}

/**
 * A body of another storey, drawn in that storey's layer `offset` px down from the viewer's (scene_room_renderer.ts): while
 * it is in the air between storeys -- coming down into that storey, or jumping up out of it -- and nothing stands in front
 * of it where it is drawn, the depth it is drawn at among the viewer's storey's art, over the storeys' layers; else
 * undefined. The layer, drawn as one, hid it there: a body of the storey below rising out of a gap, or falling into one,
 * went behind the face of the terrace it rose past, which is the viewer's storey's art.
 */
export const airborneOverStoreysDepth = (entity: Entity, active: TileMapMetaProvider | undefined, offset: number): number | undefined => {
    if (!active || !entity.location || Mechanics.entity.body.isFlat(entity)) return undefined
    const fall = entity.floorFall
    const falling = !!fall?.fromAbove && (fall.phase === "falling" || fall.phase === "arriving")
    if (!falling && mountDepth(entity) === undefined) return undefined
    const y = entity.location.y + offset
    if (!openInFront(active, Math.floor(entity.location.x / TileSize), Math.floor((y + BODY_GROUND_OFFSET) / TileSize))) return undefined
    return IN_THE_OPEN_DEPTH + (100 + y) / 1000
}

const DRAWN_ART = ["sprite", "sprite2", "sprite3", "map2", "map3"]

/**
 * Whether cell (x, y) of `provider` is in a stacked ladder shaft, whose sections run through several storeys' renderings:
 * standing there between climbs, a body stays drawn over them all. At the foot or on the hatch of a single ladder, a
 * body is sorted as anywhere else on its storey: behind the wall in front of it, under the roof it stands beside
 * (openspec movement/ladders).
 */
const standsInShaft = (provider: TileMapMetaProvider | undefined, x: number, y: number): boolean => {
    if (!provider?.isStairAt(x, y)) return false
    const delta = provider.getMapDeltaAt?.(x, y, "sprite2")
    return !!(delta?.context?.context?.stairLandings ?? delta?.context?.stairLandings)
}

/**
 * The first of the two rows south of a ladder on cell (x, row) where something stands on the ladder's own storey (a
 * wall, a block), or undefined. Every piece of that row sorts at or past `100 + row * TileSize` (sprited_tile_zindex.ts),
 * every piece of the ladder's row short of it, so a climber sorted there is over its ladder and behind that wall.
 */
const inFrontOfLadder = (provider: TileMapMetaProvider, x: number, row: number): number | undefined =>
    [row + 1, row + 2].find(y => {
        try {
            return !!provider.isCollisionAt?.(x, y) || !!provider.wallItemAt?.(x, y)
        } catch {
            return false
        }
    })

/** The top of a ladder that goes through nothing holds its stair, but no hatch is drawn there (movement/ladders). */
const undrawnLadderTop = (storey: TileMapMetaProvider, below: TileMapMetaProvider, x: number, y: number): boolean => {
    const tile = storey.resolveTileMapFromLayerId("sprite2")?.[x]?.[y]
    if (!tile || tile === "0" || storey.resolveTileMeta(tile).entityTileId !== "stairs-down") return false
    const delta = storey.getMapDeltaAt(x, y, "sprite2")
    return !(delta?.context?.context?.stairLandings ?? delta?.context?.stairLandings) && ladderTopAt(below, storey, { x, y }).kind !== "through"
}

// A map that cannot say (not loaded yet, a double in a test) has nothing there: a body's depth never throws.
const drawnAt = (storey: TileMapMetaProvider, layerId: string, x: number, y: number): boolean => {
    try {
        const tile = storey.resolveTileMapFromLayerId?.(layerId)?.[x]?.[y]
        return !!tile && tile !== "0" && !storey.isHiddenTile?.(tile)
    } catch {
        return false
    }
}

/** What stands on a storey's cell: a wall, a block, anything in the way, or furniture -- not the grass or the ivy. */
const standsAt = (storey: TileMapMetaProvider, x: number, y: number): boolean => {
    try {
        return !!storey.isCollisionAt?.(x, y) || !!storey.wallItemAt?.(x, y) || drawnAt(storey, "sprite2", x, y)
    } catch {
        return false
    }
}

/**
 * Whether nothing stands in front of a body in column `x` with its soles in row `row`, on its storey or the few above: in
 * its row or the next (what sorts in front of it there), and above, as far south as is drawn over it (a storey up is drawn
 * a row higher), their floors and roofs too. Its own storey's floor lies under it.
 */
const openInFront = (provider: TileMapMetaProvider, x: number, row: number): boolean => {
    const seen = new Set<TileMapMetaProvider>()
    let storey: TileMapMetaProvider | undefined = provider
    for (let up = 0; storey && up <= 4 && !seen.has(storey); up++, storey = storey.getUpperFloorProvider?.()) {
        seen.add(storey)
        for (let y = row; y <= row + 1 + up; y++) {
            if (standsAt(storey, x, y)) return false
            if (up && ["map2", "map3", "sky"].some(layerId => drawnAt(storey, layerId, x, y))) return false
        }
    }
    return true
}

/**
 * A body lying down (fallen, downed) sorts where it lies, a little under one standing on the same spot -- which then
 * draws over it -- but still over a wall whose foot it lies by. A wall stops a body with its soles at least 8px in front
 * of its foot (a block's face blocks its top 6px, the body's box is round the hip), and that wall's pieces sort 6.8 under
 * such a body's hip (sprited_tile_zindex.ts): under 6.8, then. Sorted at 0, under every wall, a body that fell off a
 * terrace lay behind the face of the block it fell from, all but its feet hidden.
 */
export const LYING_DEPTH_BIAS = 4

/**
 * A body in the air in a jump up onto a wall (AnimationPoseView.mount): drawn rising over the wall, so sorted as standing on
 * its top -- past every piece of the wall's row -- until it lands up there. Sorted by where it took off, a body leaping onto
 * a block beside it was drawn behind that block's face and top, into them.
 * But from behind the wall (north of it), a body stands where the wall's top is drawn -- the top is drawn a row up, a
 * storey high -- and sorted onto it at once it stood up there the moment it jumped, then hopped back up the screen to
 * where it lands: it stays behind until the arc has it as high as it lands (mountOffset), its feet on the top.
 */
const mountDepth = (entity: Entity): number | undefined => {
    const predicted = entity.predictedFlourish
    const own = !!predicted?.view.mount && Date.now() - predicted.at < predicted.view.durationMs + MOUNT_WAIT_MS
    const view = own ? predicted.view : entity.animationPose
    const mount = view?.mount
    if (!mount || mount.y === undefined || (entity.roomId && entity.roomId !== mount.roomId)) return undefined
    if (mount.x !== undefined && behindWall(entity, mount.x, mount.y)) {
        const elapsedMs = own ? Date.now() - predicted.at : view.durationMs - (view.remainingMs ?? 0)
        if (-mountOffset(elapsedMs, mount.dx, mount.dy).y < -mount.dy) return undefined
    }
    return 100 + (mount.y + 1) * TileSize
}

/** Whether a body's box stands north of the wall at (x, y), across from it: behind it, as the camera sees it. */
const behindWall = (entity: Entity, x: number, y: number): boolean => {
    const halfW = EntityDimensions.player.w / 2, halfH = EntityDimensions.player.h / 2
    const { x: hipX, y: hipY } = entity.location
    return hipX + halfW > x * TileSize && hipX - halfW < (x + 1) * TileSize && hipY + halfH <= y * TileSize + 2
}
